/* GASSISA
=========================================================================================
* Descripción: Modal de inventario existente con costos para administración de cortes.
*              Genera ticket de totales e impreso PDF a detalle.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 17/09/2026 | LIC. José Luis Aragón C.                       | Creación
 *      2       | 18/09/2026 | LIC. José Luis Aragón C.                       | Timeout largo anti-corte, ventana bloqueada y ticket con totales del reporte
 *      3       | 24/10/2026 | José Luis Aragón Cervantes                     | Bloqueo inmediato y continuo del Paso 1
 *      4       | 24/10/2026 | José Luis Aragón Cervantes                     | Seguimiento real de trabajo Redis
 * ---------------------------------------------------------------------------------------
 */

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import React, { Fragment, useState, useEffect, useRef } from "react";
import { Modal, Box, Typography, Button, Divider, Alert } from "@mui/material";
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useInventarios } from "../../context/Inventarios/InventariosContext";
import { apiEstadoReporteInventario, apiExistenciasConCostos, apiIniciarReporteInventario, apiReporteInventarioExistenteDesdeRedis } from "../../settings/ApiConfig";
import { buildTicketBytes } from "./cajaTicketUtils";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess";
import ModalStatusWaitingProcess from "../utils/ModalStatusWaitingProcess";

// 24/10/2026 | El avance se consulta desde Redis mediante sondeo secuencial:
// se programa la siguiente petición solo cuando termina la anterior.
const INTERVALO_SONDEO_REPORTE = 1200;
const MAX_FALLOS_SONDEO_REPORTE = 3;

// 18/09/2026 | El reporte es pesado: sin timeout explícito PostRequest aborta a
// los 60s (ver RequestContext createTimeoutSignal) y se pierden datos. Se usa
// el mismo margen que el PDF para la consulta de totales.
const TIMEOUT_REPORTE_PESADO = 180000;

const modalStyle = {
    position: 'fixed',
    top: '5%',
    left: '25%',
    width: '50vw',
    maxWidth: '90vw',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    overflowY: 'auto',
};

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(Number(value ?? 0) || 0);
};

export default function ModalInventarioExistente(props) {
    const { openModal = false, onClose } = props;

    const { usuarioData, parametrosCajaForm } = useSesion();
    const { handleOpenAlert } = useLayout();
    const { GetRequest, PostRequest, PostFileRequest } = useRequest();
    const { listaAlmacenesExhibidoresGeneral, handleObtenerAlmacenesExhibidores } = useInventarios();

    const [internalOpen, setInternalOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [filtroDescripcion, setFiltroDescripcion] = useState("");
    const [marca, setMarca] = useState("");
    const [almacen, setAlmacen] = useState("TODOS");
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [totales, setTotales] = useState({ totalArticulos: 0, existenciaTotal: 0, totalCostoCompra: 0, totalCostoVenta: 0 });
    // 05/10/2026 | Paso 1 (generar en Redis) y Paso 2 (ver PDF desde Redis).
    // El Paso 2 solo se habilita cuando el Paso 1 terminó con los filtros actuales.
    const [jobKey, setJobKey] = useState(null);
    const [filtrosJob, setFiltrosJob] = useState("");
    const [preparandoReporte, setPreparandoReporte] = useState(false);
    const [bloqueoOpen, setBloqueoOpen] = useState(false);
    const [bloqueoTotal, setBloqueoTotal] = useState(0);
    const [estadoReporte, setEstadoReporte] = useState("");
    const [progresoReporte, setProgresoReporte] = useState(0);
    const [mensajeReporte, setMensajeReporte] = useState("");
    const reporteEnProceso = preparandoReporte || bloqueoOpen;
    // 18/09/2026 | Cache de totales por filtros: los totales del reporte se
    // reutilizan para el ticket resumido sin reconsultar al backend.
    const totalesCacheRef = useRef({ filtros: "", datos: null });
    const pollingReporteRef = useRef(null);
    const jobSondeoRef = useRef("");
    const fallosSondeoRef = useRef(0);

    useEffect(() => {
        if (openModal) {
            setInternalOpen(true);
            // 05/10/2026 | Al abrir siempre se parte del Paso 1: el job anterior ya no vale.
            setJobKey(null);
            setFiltrosJob("");
            setPreparandoReporte(false);
            setBloqueoOpen(false);
            setEstadoReporte("");
            setProgresoReporte(0);
            setMensajeReporte("");
            if (!listaAlmacenesExhibidoresGeneral || listaAlmacenesExhibidoresGeneral.length === 0) {
                handleObtenerAlmacenesExhibidores("GENERAL");
            }
            setTimeout(() => setModalVisible(true), 50);
        } else {
            setModalVisible(false);
            if (pollingReporteRef.current) {
                clearTimeout(pollingReporteRef.current);
                pollingReporteRef.current = null;
            }
            jobSondeoRef.current = "";
            setTimeout(() => setInternalOpen(false), 300);
        }
    }, [openModal]);

    useEffect(() => () => {
        if (pollingReporteRef.current) {
            clearTimeout(pollingReporteRef.current);
        }
    }, []);

    const filtrosActuales = () => JSON.stringify({
        descripcion: filtroDescripcion.trim(),
        marca: marca.trim(),
        almacen: (almacen || "TODOS").trim(),
    });

    // 05/10/2026 | El Paso 2 solo vale con el job del Paso 1 y los mismos filtros.
    const pasoListo = Boolean(jobKey) && estadoReporte === "COMPLETADO" && filtrosJob !== "" && filtrosJob === filtrosActuales();

    const consultarDatos = async (soloTotales = false, mostrarLoader = true) => {
        const response = await PostRequest({
            url: apiExistenciasConCostos,
            body: {
                filtro: filtroDescripcion.trim(),
                marca: marca.trim(),
                almacenexhibidor: (almacen || "TODOS").trim(),
                solototales: soloTotales,
            },
            loader: mostrarLoader ? (soloTotales ? "Calculando totales..." : "Consultando inventario existente...") : undefined,
            timeout: TIMEOUT_REPORTE_PESADO,
        });
        if (!response || response.hasError) {
            handleOpenAlert((response && response.message) || "No fue posible consultar el inventario", "error");
            return null;
        }
        if (soloTotales) {
            return response.result;
        }
        return Array.isArray(response.result) ? response.result : [];
    };

    // 18/09/2026 | Obtiene totales reutilizando la última consulta del reporte
    // si los filtros no cambiaron; así el ticket resumido usa los mismos
    // totales del reporte sin riesgo de corte por tiempo de espera.
    const obtenerTotalesReporte = async () => {
        const filtros = filtrosActuales();
        const cached = totalesCacheRef.current;
        if (cached.datos && cached.filtros === filtros) {
            return cached.datos;
        }
        const resultado = await consultarDatos(true);
        if (!resultado) return null;
        const resume = leerTotales(resultado);
        if (!resume) {
            handleOpenAlert("Respuesta de totales no vélida", "error");
            return null;
        }
        setTotales(resume);
        totalesCacheRef.current = { filtros, datos: resume };
        return resume;
    };

    const leerTotales = (origen) => {
        if (!origen) return null;
        const totalArticulos = Number(origen.totalarticulos ?? origen.totalArticulos ?? 0) || 0;
        const existenciaTotal = Number(origen.existenciatotal ?? origen.existenciaTotal ?? 0) || 0;
        const totalCostoCompra = Number(origen.totalcostocompra ?? origen.totalCostoCompra ?? 0) || 0;
        const totalCostoVenta = Number(origen.totalcostoventa ?? origen.totalCostoVenta ?? 0) || 0;
        return { totalArticulos, existenciaTotal, totalCostoCompra, totalCostoVenta };
    };

    const detenerSondeoReporte = () => {
        if (pollingReporteRef.current) {
            clearTimeout(pollingReporteRef.current);
            pollingReporteRef.current = null;
        }
        jobSondeoRef.current = "";
        fallosSondeoRef.current = 0;
    };

    const programarSondeoReporte = (claveReporte, filtros) => {
        if (jobSondeoRef.current !== claveReporte) return;
        pollingReporteRef.current = setTimeout(() => {
            consultarEstadoReporte(claveReporte, filtros);
        }, INTERVALO_SONDEO_REPORTE);
    };

    const consultarEstadoReporte = async (claveReporte, filtros) => {
        if (jobSondeoRef.current !== claveReporte) return;
        try {
            const response = await GetRequest({
                url: apiEstadoReporteInventario.replace("{jobkey}", encodeURIComponent(claveReporte)),
            });
            if (!response || response.hasError) {
                throw new Error((response && response.message) || "No fue posible consultar el avance del reporte");
            }
            if (jobSondeoRef.current !== claveReporte) return;

            fallosSondeoRef.current = 0;
            const estado = response.result || {};
            const estatus = String(estado.estado ?? estado.sEstado ?? "PENDIENTE").toUpperCase();
            const progreso = Number(estado.progreso ?? estado.iProgreso ?? 0) || 0;
            const mensaje = estado.mensaje ?? estado.sMensaje ?? "Procesando reporte de inventario...";
            const totalRegistros = Number(estado.totalregistros ?? estado.iTotalRegistros ?? 0) || 0;

            setEstadoReporte(estatus);
            setProgresoReporte(progreso);
            setMensajeReporte(mensaje);
            setBloqueoTotal(totalRegistros);

            if (estatus === "COMPLETADO") {
                const resume = leerTotales(estado.totales ?? estado.Totales);
                if (resume) {
                    setTotales(resume);
                    totalesCacheRef.current = { filtros, datos: resume };
                }
                detenerSondeoReporte();
                setBloqueoOpen(false);
                handleOpenAlert("Reporte generado, ya puede ver el detalle PDF en el Paso 2", "success");
                return;
            }

            if (estatus === "ERROR") {
                const detalleError = estado.error ?? estado.sError ?? mensaje;
                detenerSondeoReporte();
                setBloqueoOpen(false);
                handleOpenAlert(detalleError || "No fue posible generar el reporte", "error");
                return;
            }

            programarSondeoReporte(claveReporte, filtros);
        } catch (error) {
            if (jobSondeoRef.current !== claveReporte) return;
            fallosSondeoRef.current += 1;
            if (fallosSondeoRef.current < MAX_FALLOS_SONDEO_REPORTE) {
                setMensajeReporte("Verificando conexión con el estado del reporte...");
                programarSondeoReporte(claveReporte, filtros);
                return;
            }
            detenerSondeoReporte();
            setEstadoReporte("ERROR");
            setBloqueoOpen(false);
            handleOpenAlert("No fue posible confirmar el estado del reporte: " + error.message, "error");
        }
    };

    const iniciarSondeoReporte = (claveReporte, filtros) => {
        detenerSondeoReporte();
        jobSondeoRef.current = claveReporte;
        consultarEstadoReporte(claveReporte, filtros);
    };

    const handleImprimirTicket = async () => {
        // 18/09/2026 | Misma forma de impresión que los cortes impresos en
        // ticket: bytes vía builder compartido, impresora de ventas de la caja,
        // logo y endpoint local de PrinterTools.
        const impresora = (parametrosCajaForm?.nombreimpresoraventas || "").trim();
        if (!impresora) {
            handleOpenAlert("Configure la impresora de ventas en los parémetros de la caja", "error");
            return;
        }
        setLoading(true);
        setLoadingMessage("Calculando totales del reporte...");
        try {
            const resume = await obtenerTotalesReporte();
            if (!resume) return;

            setLoadingMessage("Enviando ticket a la impresora...");
            const textEncoder = new TextEncoder();
            const fecha = new Date().toLocaleString('es-MX');
            let ticketParts = [
                textEncoder.encode('\x1B\x40'),
                textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'INVENTARIO EXISTENTE\n'),
                textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
                textEncoder.encode('Sucursal: ' + (parametrosCajaForm?.sucursal || "") + '\n'),
                textEncoder.encode('Almacen: ' + ((almacen || "TODOS").trim()) + '\n'),
                textEncoder.encode('Usuario: ' + (usuarioData?.Usuario || "") + '\n'),
                textEncoder.encode('Fecha: ' + fecha + '\n'),
                textEncoder.encode('Filtros: ' + (filtroDescripcion.trim() + ' ' + marca.trim()).trim() + '\n'),
                textEncoder.encode('-'.repeat(32) + '\n'),
                textEncoder.encode('\n--- TOTALES ---\n'),
                textEncoder.encode('Total articulos:  ' + String(resume.totalArticulos).padStart(10) + '\n'),
                textEncoder.encode('Existencia total: ' + resume.existenciaTotal.toFixed(2).padStart(10) + '\n'),
                textEncoder.encode('Tot. costo compra:' + formatCurrency(resume.totalCostoCompra).padStart(13) + '\n'),
                textEncoder.encode('Tot. costo venta: ' + formatCurrency(resume.totalCostoVenta).padStart(13) + '\n'),
                textEncoder.encode('-'.repeat(32) + '\n'),
            ];

            const pieConfigurado = (parametrosCajaForm?.pieticket || "").trim();
            if (pieConfigurado.length > 0) {
                ticketParts.push(textEncoder.encode('\x1B\x61\x01' + pieConfigurado + '\n'));
                ticketParts.push(textEncoder.encode('\x1B\x61\x00' + '-'.repeat(32) + '\n'));
            }

            ticketParts.push(
                textEncoder.encode('\n\n'),
                textEncoder.encode('\x1B\x61\x01' + 'Reporte generado!\n\n\n'),
                textEncoder.encode('\x1B\x64\x06'),
                textEncoder.encode('\x1D\x56\x00'),
            );

            const finalTicket = buildTicketBytes(ticketParts);

            const formData = new FormData();
            formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
            formData.append("printerName", impresora);

            const logoUrl = `${process.env.PUBLIC_URL}/recursos/logocliente.png`;
            try {
                const logoResponse = await fetch(logoUrl);
                if (logoResponse.ok) {
                    const logoBlob = await logoResponse.blob();
                    formData.append("logo", logoBlob, "logocliente.png");
                }
            } catch (err) {
                handleOpenAlert("Logo no disponible:", err);
            }

            const response2 = await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });

            if (!response2.ok) {
                handleOpenAlert("Error en envío de impresión:", response2.statusText);
                return;
            }
            handleOpenAlert("Ticket de inventario generado exitosamente", "success");
            onClose();
        } catch (error) {
            handleOpenAlert("Error en impresión:", error.message);
        } finally {
            setLoading(false);
            setLoadingMessage("");
        }
    };

    const handleGenerarPDF = async () => {
        // 05/10/2026 | Paso 2: el PDF se lee de Redis con el jobKey del Paso 1,
        // misma estructura. Si cambiaron los filtros o expiró, se pide regenerar.
        if (!pasoListo || !jobKey) {
            handleOpenAlert("Primero genere el reporte en el Paso 1 con los filtros actuales.", "warning");
            return;
        }
        setLoading(true);
        setLoadingMessage("Leyendo reporte y generando PDF, no cierre esta ventana...");
        try {
            const result = await PostFileRequest({
                url: apiReporteInventarioExistenteDesdeRedis,
                body: { jobkey: jobKey },
                filename: "ReporteInventarioExistente.pdf",
                openInNewTab: true,
                loader: "Generando PDF...",
                timeout: TIMEOUT_REPORTE_PESADO,
            });

            if (!result) throw new Error("No se pudo generar el reporte");

            handleOpenAlert("Reporte de inventario existente generado exitosamente", "success");
            onClose();
        } catch (error) {
            handleOpenAlert("Error al generar reporte: " + error.message, "error");
        } finally {
            setLoading(false);
            setLoadingMessage("");
        }
    };

    // 24/10/2026 | Paso 1: la API encola el trabajo y devuelve la clave de
    // inmediato; el modal obtiene el avance real consultando Redis.
    const handleGenerarReporte = async () => {
        if (reporteEnProceso) return;
        const filtros = filtrosActuales();
        let trabajoEncolado = false;
        detenerSondeoReporte();
        setJobKey(null);
        setFiltrosJob("");
        setEstadoReporte("");
        setProgresoReporte(0);
        setMensajeReporte("");
        setBloqueoTotal(0);
        setPreparandoReporte(true);
        try {
            await new Promise((resolve) => requestAnimationFrame(resolve));
            const response = await PostRequest({
                url: apiIniciarReporteInventario,
                body: {
                    filtro: filtroDescripcion.trim(),
                    marca: marca.trim(),
                    almacenexhibidor: (almacen || "TODOS").trim(),
                    sucursal: parametrosCajaForm?.sucursal ?? '',
                    usuario: usuarioData?.Usuario ?? '',
                },
                timeout: 30000,
            });

            if (!response || response.hasError) {
                handleOpenAlert((response && response.message) || "No fue posible iniciar el reporte", "error");
                return;
            }

            const job = response.result || {};
            const nuevoJobKey = job.jobkey ?? job.jobKey ?? "";
            if (!nuevoJobKey) {
                handleOpenAlert("El servidor no devolvió clave de reporte", "error");
                return;
            }

            setJobKey(nuevoJobKey);
            setFiltrosJob(filtros);
            setEstadoReporte(String(job.estado ?? job.sEstado ?? "PENDIENTE").toUpperCase());
            setProgresoReporte(Number(job.progreso ?? job.iProgreso ?? 0) || 0);
            setMensajeReporte(job.mensaje ?? job.sMensaje ?? "Reporte en espera de procesamiento");
            setBloqueoTotal(Number(job.totalregistros ?? job.iTotalRegistros ?? 0) || 0);
            trabajoEncolado = true;
            setBloqueoOpen(true);
            setPreparandoReporte(false);
            iniciarSondeoReporte(nuevoJobKey, filtros);
        } catch (error) {
            handleOpenAlert("Error al iniciar reporte: " + error.message, "error");
        } finally {
            setPreparandoReporte(false);
            if (!trabajoEncolado) {
                setBloqueoOpen(false);
            }
        }
    };

    // 18/09/2026 | Ventana bloqueada mientras se procesa el reporte pesado:
    // se ignora cierre por backdrop/Escape y Cancelar ya va deshabilitado.
    // 05/10/2026 | También se bloquea mientras el Paso 1 muestra su modal de progreso.
    const handleIntentarCerrar = () => {
        if (loading || reporteEnProceso) return;
        onClose();
    };

    return (
        <Fragment>
        <Modal
            open={internalOpen}
            onClose={handleIntentarCerrar}
            closeAfterTransition
            disableEscapeKeyDown={loading || reporteEnProceso}
        >
            <Box sx={{ ...modalStyle, transform: modalVisible ? 'scale(1)' : 'scale(0.8)', opacity: modalVisible ? 1 : 0, transition: 'all 0.3s ease-in-out' }}>
                <Fragment>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Inventario existente
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Reporte del inventario actual existente con costos por medida en 2 pasos:
                            1) Generar reporte (guarda los datos y muestra el avance),
                            2) Ver detalle PDF (se habilita al terminar el paso 1).
                            Costo compra = CUC (si es 0 usa PMVB), costo venta = precio definitivo.
                            Filtros opcionales: vacíos traen todo el inventario. Filtre por almacén para listados més répidos.
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <TextField autoComplete="off"
                            label="Descripción"
                            value={filtroDescripcion}
                            onChange={(e) => setFiltroDescripcion(e.target.value)}
                            disabled={loading || reporteEnProceso}
                            size="small"
                            sx={{ flex: '1 1 200px' }}
                        />
                        <TextField autoComplete="off"
                            label="Marca"
                            value={marca}
                            onChange={(e) => setMarca(e.target.value)}
                            disabled={loading || reporteEnProceso}
                            size="small"
                            sx={{ flex: '1 1 160px' }}
                        />
                        <Autocomplete
                            freeSolo
                            disabled={loading || reporteEnProceso}
                            options={["TODOS", ...(listaAlmacenesExhibidoresGeneral || []).map((item) => item.nombre)]}
                            value={almacen}
                            onChange={(_, valor) => setAlmacen(valor || "TODOS")}
                            onInputChange={(_, valor) => setAlmacen(valor || "TODOS")}
                            sx={{ flex: '1 1 200px' }}
                            renderInput={(params) => (
                                <TextField autoComplete="off" {...params} label="Almacén o Exhibidor" size="small" disabled={loading || reporteEnProceso} />
                            )}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2">Total artículos: <strong>{totales.totalArticulos}</strong></Typography>
                        <Typography variant="body2">Existencia total: <strong>{Number(totales.existenciaTotal).toFixed(2)}</strong></Typography>
                        <Typography variant="body2">Total costo compra: <strong>{formatCurrency(totales.totalCostoCompra)}</strong></Typography>
                        <Typography variant="body2">Total costo venta: <strong>{formatCurrency(totales.totalCostoVenta)}</strong></Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            onClick={handleIntentarCerrar}
                            disabled={loading || reporteEnProceso}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleImprimirTicket}
                            disabled={loading || reporteEnProceso}
                        >
                            {loading && !bloqueoOpen ? "Generando..." : "Ticket totales"}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleGenerarReporte}
                            disabled={loading || reporteEnProceso}
                        >
                            1. Generar reporte
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerarPDF}
                            disabled={loading || reporteEnProceso || !pasoListo}
                        >
                            {loading ? "Generando..." : "2. Ver detalle PDF"}
                        </Button>
                    </Box>
                </Fragment>
            </Box>
        </Modal>
        {/* 18/09/2026 | Bloqueo de pégina con el componente del sistema (mismo que FrmVentaSuper). */}
        <SizeSoftXLoadingProcess
            open={loading || preparandoReporte}
            message={preparandoReporte
                ? "Preparando reporte de inventario, por favor espere..."
                : (loadingMessage ? `${loadingMessage} No cierre esta ventana.` : "Procesando reporte, por favor espere...")}
        />
        {/* 24/10/2026 | Bloqueo del Paso 1 con avance confirmado por Redis. */}
        <ModalStatusWaitingProcess
            open={bloqueoOpen}
            totalRegistros={bloqueoTotal}
            titulo="Generando reporte de inventario..."
            progreso={progresoReporte}
            mensaje={mensajeReporte}
        />
        </Fragment>
    );
}
